// Забор · вкладка «Заборы» (2026-06-05, Phase C). Лента курьерских заборов,
// которые ещё не доехали до склада (approved/pickup). Отсортированы по дате
// забора — «чем выше, тем ближе приедет забор». Клик по строке открывает
// сделку (FlexBody) через общий OrderDrawer.
import { useState } from "react";
import { FLEX_STATUS_LABELS_RU, getCountry, type FlexStatus } from "@mnp/shared";
import { useFlexPickups, type FlexPickupRow } from "../../lib/useFlexOrders";
import { OrderDrawer } from "../orders/OrderDrawer";

function isoDay(v: string | null | undefined): string {
  if (!v) return "";
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(v));
  return m?.[1] ?? "";
}

// «2026-06-15» → «15.06.2026»
function ruDay(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "—";
  const [y, mo, d] = iso.split("-");
  return `${d}.${mo}.${y}`;
}

// Дата забора: редактируемая pickupDate важнее исходного pickupDay клиента.
function pickupIsoDay(row: FlexPickupRow): string {
  return isoDay(row.pickupDate) || isoDay(row.pickupDay);
}

function countryName(code?: string | null): string {
  if (!code) return "";
  return getCountry(code)?.nameRu ?? code;
}

// Тон даты: просрочено (раньше сегодня) — красный, сегодня — янтарный.
function dateTone(iso: string, todayIso: string): "red" | "amber" | "gray" {
  if (!iso) return "gray";
  if (iso < todayIso) return "red";
  if (iso === todayIso) return "amber";
  return "gray";
}

export function PickupsSubscreen() {
  const { data, isLoading, isError } = useFlexPickups();
  const [openId, setOpenId] = useState<string | null>(null);

  const rows: FlexPickupRow[] = Array.isArray(data?.rows) ? data!.rows : [];
  // «Сегодня» считаем в зоне Asia/Tbilisi (UTC+4), а не в UTC: иначе в ранние
  // часы по Тбилиси подсветка дат (просрочено/сегодня) уезжает на день назад.
  const todayIso = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tbilisi" }).format(new Date());

  return (
    <div className="reception-stub" data-testid="pickups-subscreen">
      <header className="reception-stub-head">
        <h2 className="cb-h2">Заборы</h2>
        <p className="cb-h2-sub">Курьерские заборы — ближайшие сверху</p>
      </header>

      <section className="panel" style={{ marginTop: 16 }}>
        <header className="panel-head">
          <p className="panel-title">
            Ожидают забора{rows.length ? ` · ${rows.length}` : ""}
          </p>
        </header>

        {isLoading ? (
          <div className="muted" style={{ padding: 16 }} data-testid="pickups-loading">
            Загрузка…
          </div>
        ) : isError ? (
          <div className="muted" style={{ padding: 16 }} data-testid="pickups-error">
            Не удалось загрузить заборы
          </div>
        ) : rows.length === 0 ? (
          <div className="muted" style={{ padding: 16 }} data-testid="pickups-empty">
            Активных заборов нет
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Дата забора</th>
                <th>Слот</th>
                <th>Откуда</th>
                <th>Отправитель</th>
                <th>Перевозчик</th>
                <th>Заявка</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const iso = pickupIsoDay(r);
                const tone = dateTone(iso, todayIso);
                const carrier = r.pickupCarrierRef?.trim();
                return (
                  <tr
                    key={r.id}
                    onClick={() => setOpenId(r.id)}
                    style={{ cursor: "pointer" }}
                    data-testid={`pickup-row-${r.id}`}
                  >
                    <td>
                      <span className={`badge-a ${tone}`}>{ruDay(iso)}</span>
                    </td>
                    <td className="muted">{r.pickupSlot || "—"}</td>
                    <td className="muted">
                      {countryName(r.originCountry) || "—"}
                      {r.pickupAddress ? (
                        <span style={{ display: "block", fontSize: 11, opacity: 0.8 }}>
                          {r.pickupAddress}
                        </span>
                      ) : null}
                    </td>
                    <td>{r.senderFullName || "—"}</td>
                    <td>
                      {carrier ? (
                        carrier
                      ) : (
                        <span className="badge-a gray">не назначен</span>
                      )}
                    </td>
                    <td className="mono">{r.requestNumber || "—"}</td>
                    <td>
                      <span className="badge-a blue">
                        {FLEX_STATUS_LABELS_RU[r.status as FlexStatus] ?? r.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {openId && <OrderDrawer id={openId} type="flex" onClose={() => setOpenId(null)} />}
    </div>
  );
}
